import { formatCurrency } from '../lib/formatters';

interface Campaign {
  campaign_id: string;
  campaign_name: string;
  platform: string;
  spend: number;
  budget: number;
  utilization_pct: number;
  status: 'ok' | 'warning' | 'exceeded';
  remaining: number;
  daily_spend?: number;
  daily_budget?: number;
  projected_end?: string;
}

interface Props {
  campaign: Campaign;
}

const statusColors = {
  ok: {
    bg: 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800',
    bar: 'bg-green-500',
    text: 'text-green-700 dark:text-green-400',
    badge: 'bg-green-500 text-white',
  },
  warning: {
    bg: 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800',
    bar: 'bg-yellow-500',
    text: 'text-yellow-700 dark:text-yellow-400',
    badge: 'bg-yellow-500 text-white',
  },
  exceeded: {
    bg: 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800',
    bar: 'bg-red-500',
    text: 'text-red-700 dark:text-red-400',
    badge: 'bg-red-500 text-white',
  },
};

const platformIcons: Record<string, string> = {
  meta: '🔵',
  google: '🔴',
  tiktok: '⚫',
};

export function BudgetCard({ campaign }: Props) {
  const colors = statusColors[campaign.status];
  const utilizationPercent = Math.min(campaign.utilization_pct * 100, 100);

  return (
    <div className={`p-4 rounded-lg border shadow ${colors.bg}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xl">{platformIcons[campaign.platform] || '📊'}</span>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {campaign.campaign_name}
            </h3>
          </div>
          <span className="text-sm text-gray-500 uppercase dark:text-gray-400">
            {campaign.platform}
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          <span className={`px-3 py-1 rounded-full text-sm font-semibold ${colors.badge}`}>
            {utilizationPercent.toFixed(0)}%
          </span>
          {campaign.status === 'exceeded' && (
            <span className="text-2xl">🚨</span>
          )}
          {campaign.status === 'warning' && (
            <span className="text-2xl">⚠️</span>
          )}
          {campaign.status === 'ok' && (
            <span className="text-2xl">✅</span>
          )}
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-3">
        <div className="h-4 bg-gray-200 rounded-full overflow-hidden dark:bg-gray-700">
          <div
            className={`h-full transition-all duration-500 ${colors.bar}`}
            style={{ width: `${utilizationPercent}%` }}
          />
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 gap-4 mb-3">
        <div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Spend</div>
          <div className="text-lg font-semibold text-gray-900 dark:text-white">
            {formatCurrency(campaign.spend)}
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Budget</div>
          <div className="text-lg font-semibold text-gray-900 dark:text-white">
            {formatCurrency(campaign.budget)}
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Remaining</div>
          <div className={`text-lg font-semibold ${colors.text}`}>
            {formatCurrency(campaign.remaining)}
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Daily Avg</div>
          <div className="text-lg font-semibold text-gray-900 dark:text-white">
            {formatCurrency(campaign.daily_spend || 0)}
          </div>
        </div>
      </div>

      {/* Projected End Date */}
      {campaign.projected_end && campaign.remaining > 0 && (
        <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500 dark:text-gray-400">
              Projected budget depletion:
            </span>
            <span className="font-medium text-gray-900 dark:text-white">
              {new Date(campaign.projected_end).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
              })}
            </span>
          </div>
        </div>
      )}

      {/* Exceeded Message */}
      {campaign.status === 'exceeded' && (
        <div className="pt-3 border-t border-red-200 dark:border-red-800">
          <div className="flex items-center gap-2 text-sm font-medium text-red-700 dark:text-red-400">
            <span>⚠️</span>
            <span>Budget exceeded by {formatCurrency(Math.abs(campaign.remaining))}</span>
          </div>
        </div>
      )}
    </div>
  );
}
